/**
 * Section 4 — when the post goes out.
 *
 * The three modes are a radio group rather than a dropdown because they are not
 * variations on one action: "publish now" is irreversible, "schedule" is
 * reversible until it fires, and "draft" publishes nothing. Showing all three at
 * once is what stops the irreversible one being picked by accident.
 */

import { useTranslation } from '../../../context/LanguageContext';
import { cn } from '../../../lib/utils';
import type { ScheduleMode, ScheduleOptions } from '../../../types/composer';
import type { TranslationKey } from '../../../i18n';

const MODES: { mode: ScheduleMode; labelKey: TranslationKey }[] = [
  { mode: 'now', labelKey: 'composer.schedule.now' },
  { mode: 'scheduled', labelKey: 'composer.schedule.schedule' },
  { mode: 'draft', labelKey: 'composer.schedule.draft' },
];

interface ScheduleSectionProps {
  schedule: ScheduleOptions;
  onChange: (schedule: ScheduleOptions) => void;
}

export function ScheduleSection({ schedule, onChange }: Readonly<ScheduleSectionProps>) {
  const { t } = useTranslation();

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-7 gap-y-2">
        {MODES.map(({ mode, labelKey }) => (
          <label
            key={mode}
            className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-700"
          >
            <input
              type="radio"
              name="schedule-mode"
              checked={schedule.mode === mode}
              onChange={() => onChange({ ...schedule, mode })}
              className="h-4 w-4 border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500"
            />
            <span className={cn(schedule.mode === mode && 'font-semibold text-slate-900')}>
              {t(labelKey)}
            </span>
          </label>
        ))}
      </div>

      {schedule.mode === 'scheduled' && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3">
          <label className="flex-1 min-w-[200px]">
            <span className="mb-1 block text-[11px] font-semibold text-slate-600">
              {t('composer.schedule.dateTime')}
            </span>
            <input
              type="datetime-local"
              value={toLocalInputValue(schedule.scheduledAt)}
              onChange={(e) =>
                onChange({
                  ...schedule,
                  scheduledAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <div className="min-w-[160px]">
            <span className="mb-1 block text-[11px] font-semibold text-slate-600">
              {t('composer.schedule.timezone')}
            </span>
            <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] text-slate-500">
              {schedule.timezone}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * `datetime-local` wants wall-clock time with no zone, but state holds a UTC
 * ISO string. Slicing the ISO string would show the user a UTC time labelled as
 * theirs, so the components are read back in local time explicitly.
 */
function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
